import { defineStore } from 'pinia';
import MutationCall from '~/graphql/wrappers/mutation-call';
import  {
  CloseCourseDocument,
  type CloseCourseMutation,
  type CloseCourseMutationVariables,
  CourseListStatusFilter,
  CourseListUsageFilter,
  CreateCourseDocument,
  type CreateCourseMutationVariables,
  MyCoursesDocument,
  UpdateCourseDocument,
  type UpdateCourseInput,
  type UpdateCourseMutation,
  type UpdateCourseMutationVariables,
  UsageType,
} from '~/graphql';

let createCourseMutation: MutationCall<CreateCourseMutation, CreateCourseMutationVariables>;
let updateCourseMutation: MutationCall<UpdateCourseMutation, UpdateCourseMutationVariables>;
let closeCourseMutation: MutationCall<CloseCourseMutation, CloseCourseMutationVariables>;

export const courseCacheExchange = {
  updates: {
    Mutation: {
      reorderCourse(result, _args, cache) {
        //console.log('reorder', result, _args, cache);
        const position = _args.reorderInput.position;
        const courseId = _args.reorderInput.id;

        cache.updateQuery(
          {
            query: MyCoursesDocument,
            variables: {
              coursesQueryFilter: {
                status: CourseListStatusFilter.Open,
                usage: CourseListUsageFilter.Regular,
              },
            },
          },
          (data) => {
            if (data) {
              let course = null;
              for (let i = 0; i < data.myCourses.length; i++) {
                if (data.myCourses[i]?.id === courseId) {
                  course = data.myCourses.splice(i, 1)[0];
                }
              }
              data.myCourses.splice(position, 0, course);
            }
            return null;
          }
        );
      },
    },
  },
};

export const useCourseManagement = defineStore('courseManagement', () => {
  async function init() {
    createCourseMutation = new MutationCall(CreateCourseDocument);
    updateCourseMutation = new MutationCall(UpdateCourseDocument);
    closeCourseMutation = new MutationCall(CloseCourseDocument);
  }

  async function createCourse(
    tenantId: string,
    name: string,
    shortName: string,
    foregroundColor: string,
    backgroundColor: string,
    schoolYearId: string,
    subjectId: string | null,
    type: 'course' | 'template' = 'course',
    templateId: string | null = null
  ) {
    const result = await createCourseMutation.run({
      createCourseInput: {
        name,
        shortName,
        foregroundColor,
        backgroundColor,
        days: [],
        usage: type === 'course' ? UsageType.Regular : UsageType.Template,
        templateId,
        position: -1,
      },
    });

    if (result.hasError()) {
      console.log(result.getError());
      throw result.getError();
    }

    return result.getData().createCourse.course;
  }

  async function updateCourseDetails(
    id: string,
    updatedDetails: Partial<UpdateCourseInput>
  ) {
    const result = await updateCourseMutation.run({
      updateCourseInput: {
        id,
        ...updatedDetails,
      },
    });

    if (result.hasError()) {
      console.log(result.getError());
      throw result.getError();
    }

    return result.getData().updateCourse.course;
  }

  async function closeCourse(id: string) {
    const result = await closeCourseMutation.run({
      courseIdInput: {
        id,
      },
    });

    if (result.hasError()) {
      console.log(result.getError());
      throw result.getError();
    }
  }

  return {
    init,
    createCourse,
    updateCourseDetails,
    closeCourse,
  };
});
